import React, { useEffect, useMemo, useState } from 'react';
import { Star } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { generateGbpPost, GbpPost } from '@/lib/gbp-manager';

type ActiveTab = 'posts' | 'qa' | 'reviews';

type TargetSite = 'halesac' | 'myccs' | 'abrahamac';

interface GbpQaRow {
  id: string;
  company_id: string | null;
  question: string;
  answer: string | null;
  status: 'unanswered' | 'answered' | 'failed' | string;
  question_id: string | null;
  answer_id: string | null;
  created_at: string;
  updated_at: string;
}

interface GbpReviewRow {
  id: string;
  location_id: string;
  review_id: string;
  target_site: TargetSite;
  rating: number | null;
  comment: string | null;
  reviewer_name: string | null;
  create_time: string | null;
  reply_text: string | null;
  reply_time: string | null;
  created_at: string;
}

interface GbpLocationMappingRow {
  location_id: string;
  location_name: string;
}

interface GoogleBusinessProfileRow {
  place_id: string | null;
  location_name: string;
}

const TARGET_SITES: TargetSite[] = ['halesac', 'myccs', 'abrahamac'];

const TARGET_SITE_LABELS: Record<TargetSite, string> = {
  halesac: 'Hales AC',
  myccs: 'My CCS',
  abrahamac: 'Abraham AC'
};

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

function RatingStars({ rating }: { rating: number | null }) {
  const safe = rating ?? 0;

  return (
    <div className="flex items-center gap-0.5" aria-label={`Rating ${safe} out of 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={16}
          className={i <= safe ? 'text-yellow-500' : 'text-gray-300'}
          fill={i <= safe ? 'currentColor' : 'none'}
        />
      ))}
    </div>
  );
}

const GBPManagerPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('posts');

  const [posts, setPosts] = useState<GbpPost[]>([]);
  const [qaItems, setQaItems] = useState<GbpQaRow[]>([]);
  const [reviews, setReviews] = useState<GbpReviewRow[]>([]);

  const [locationNameById, setLocationNameById] = useState<Record<string, string>>({});

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedSite, setSelectedSite] = useState<'all' | TargetSite>('all');

  useEffect(() => {
    void fetchLocationMapping();
  }, []);

  useEffect(() => {
    if (activeTab === 'posts') void fetchPosts();
    if (activeTab === 'qa') void fetchQa();
    if (activeTab === 'reviews') void fetchReviews();
  }, [activeTab, selectedSite]);

  const filteredReviews = useMemo(() => {
    if (selectedSite === 'all') return reviews;
    return reviews.filter((r) => r.target_site === selectedSite);
  }, [reviews, selectedSite]);

  async function fetchLocationMapping(): Promise<void> {
    try {
      const { data, error: mappingError } = await supabase
        .from('gbp_location_mapping')
        .select('location_id, location_name')
        .eq('is_active', true);

      if (mappingError) throw mappingError;

      const map: Record<string, string> = {};
      for (const row of (data || []) as GbpLocationMappingRow[]) {
        map[row.location_id] = row.location_name;
      }

      // Also include Google Places-backed profile mappings (place_id -> location_name)
      // so that Places-fetched reviews can still display a friendly location label.
      const { data: profileData, error: profileError } = await supabase
        .from('google_business_profiles')
        .select('place_id, location_name')
        .not('place_id', 'is', null);

      if (!profileError) {
        for (const row of (profileData || []) as GoogleBusinessProfileRow[]) {
          if (row.place_id) map[row.place_id] = row.location_name;
        }
      }

      setLocationNameById(map);
    } catch (e) {
      console.error('Error fetching GBP location mapping:', e);
      // Non-fatal: we can still display raw location IDs.
    }
  }

  async function fetchPosts(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('gbp_posts')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setPosts((data || []) as GbpPost[]);
    } catch (e: unknown) {
      console.error('Error fetching GBP posts:', e);
      setError('Failed to load GBP posts.');
    } finally {
      setLoading(false);
    }
  }

  async function fetchQa(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('gbp_qa')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setQaItems((data || []) as GbpQaRow[]);
    } catch (e: unknown) {
      console.error('Error fetching GBP Q&A:', e);
      setError('Failed to load GBP Q&A.');
    } finally {
      setLoading(false);
    }
  }

  async function fetchReviews(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('gbp_reviews')
        .select('*')
        .order('create_time', { ascending: false })
        .limit(100);

      if (selectedSite !== 'all') {
        query = query.eq('target_site', selectedSite);
      }

      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;

      setReviews((data || []) as GbpReviewRow[]);
    } catch (e: unknown) {
      console.error('Error fetching GBP reviews:', e);
      setError('Failed to load GBP reviews.');
    } finally {
      setLoading(false);
    }
  }

  async function handleGeneratePost(companyId: TargetSite): Promise<void> {
    setError(null);
    try {
      const newPost = await generateGbpPost(companyId);
      const { error: insertError } = await supabase.from('gbp_posts').insert(newPost);
      if (insertError) throw insertError;

      await fetchPosts();
    } catch (e: unknown) {
      console.error('Error generating GBP post:', e);
      setError('Failed to generate GBP post.');
    }
  }

  return (
    <div className="p-6">
      <div className="mb-4">
        <h1 className="text-2xl font-bold">Google Business Profile Manager</h1>
        <p className="text-sm text-gray-600">
          Posts, Q&A, and Reviews for your connected GBP locations.
        </p>
      </div>

      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {(
            [
              { id: 'posts', label: 'Posts' },
              { id: 'qa', label: 'Q&A' },
              { id: 'reviews', label: 'Reviews' }
            ] as const
          ).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {error && (
        <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {activeTab === 'posts' && (
        <div>
          <div className="flex flex-wrap gap-2 mb-4">
            {TARGET_SITES.map((site) => (
              <button
                key={site}
                onClick={() => void handleGeneratePost(site)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
                type="button"
              >
                Generate Post for {TARGET_SITE_LABELS[site]}
              </button>
            ))}
          </div>

          {loading ? (
            <p>Loading posts...</p>
          ) : posts.length === 0 ? (
            <div className="rounded border p-4 text-sm text-gray-600">
              No GBP posts yet.
            </div>
          ) : (
            <div className="space-y-4">
              {posts.map((post) => (
                <div key={post.id} className="p-4 border rounded">
                  <div className="text-sm text-gray-500 mb-2">
                    <span className="font-medium">{post.company_id}</span>
                    <span className="mx-2">•</span>
                    <span className="capitalize">{post.status}</span>
                  </div>
                  <p className="text-gray-900">{post.content}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'qa' && (
        <div>
          {loading ? (
            <p>Loading Q&A...</p>
          ) : qaItems.length === 0 ? (
            <div className="rounded border p-4 text-sm text-gray-600">
              No GBP Q&A yet.
            </div>
          ) : (
            <div className="space-y-4">
              {qaItems.map((qa) => (
                <div key={qa.id} className="p-4 border rounded">
                  <div className="text-sm text-gray-500 mb-2">
                    <span className="font-medium">{qa.company_id || '—'}</span>
                    <span className="mx-2">•</span>
                    <span className="capitalize">{qa.status}</span>
                  </div>
                  <p className="font-semibold text-gray-900">Q: {qa.question}</p>
                  <p className="text-gray-700 mt-2">
                    A: {qa.answer || <span className="text-gray-400">(No answer yet)</span>}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'reviews' && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <label className="text-sm font-medium text-gray-700">Filter by site</label>
            <select
              value={selectedSite}
              onChange={(e) => setSelectedSite(e.target.value as 'all' | TargetSite)}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="all">All</option>
              {TARGET_SITES.map((s) => (
                <option key={s} value={s}>
                  {TARGET_SITE_LABELS[s]}
                </option>
              ))}
            </select>
          </div>

          {loading ? (
            <p>Loading reviews...</p>
          ) : filteredReviews.length === 0 ? (
            <div className="rounded border p-4 text-sm text-gray-600">
              No reviews found.
              <div className="mt-2 text-xs text-gray-500">
                Note: reviews populate when the GBP sync job runs.
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredReviews.map((review) => {
                const locationLabel =
                  locationNameById[review.location_id] || review.location_id;
                return (
                  <div key={review.id} className="p-4 border rounded">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900">
                            {review.reviewer_name || 'Anonymous'}
                          </span>
                          <span className="text-gray-300">•</span>
                          <span className="text-sm text-gray-500">
                            {formatDateTime(review.create_time)}
                          </span>
                        </div>
                        <div className="mt-1">
                          <RatingStars rating={review.rating} />
                        </div>
                      </div>
                      <div className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded whitespace-nowrap">
                        {TARGET_SITE_LABELS[review.target_site]} • {locationLabel}
                      </div>
                    </div>

                    {review.comment && (
                      <p className="text-gray-800 mt-3">{review.comment}</p>
                    )}

                    <div className="mt-4">
                      {review.reply_text ? (
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
                          <div className="text-xs font-semibold text-blue-900 mb-1">
                            Reply • {formatDateTime(review.reply_time)}
                          </div>
                          <div className="text-sm text-blue-900">{review.reply_text}</div>
                        </div>
                      ) : (
                        <div className="bg-orange-50 border-l-4 border-orange-500 p-3 rounded text-sm text-orange-900">
                          No reply yet.
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GBPManagerPage;
